import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Music2, Trash2, Upload } from 'lucide-react';
import { useCelebrations } from './Celebrations';

const API = process.env.REACT_APP_BACKEND_URL;

/* Set Goals → Celebration alerts: company-wide track, clip window, volume, alert types, tests. */
export default function AlertSettingsSection({ authToken, isAdmin }) {
  const cel = useCelebrations();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  if (!cel) return null;
  const s = cel.settings || {};
  const H = { Authorization: `Bearer ${authToken}` };

  const save = async (patch) => {
    try {
      const r = await axios.put(`${API}/api/gameplan/alerts/settings`, patch, { headers: H });
      cel.setSettings(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save alert settings'); }
  };
  const upload = async (file) => {
    if (!file) return;
    if (!/^audio\//.test(file.type) && !/\.(mp3|m4a|wav|aac|ogg)$/i.test(file.name)) { toast.error('Choose an audio file, such as an MP3.'); return; }
    if (file.size > 12 * 1024 * 1024) { toast.error('Track must be 12 MB or smaller — trim it to the section you want to play.'); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/api/gameplan/alerts/track`, fd, { headers: H });
      cel.setSettings(r.data);
      toast.success('Alert track loaded', { description: `${file.name} · plays on every screen and TV` });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { const r = await axios.delete(`${API}/api/gameplan/alerts/track`, { headers: H }); cel.setSettings(r.data); toast.success('Alert track removed'); } catch (_) { toast.error('Could not remove track'); } finally { setBusy(false); }
  };
  const num = (k, min, step = 0.5) => (
    <input type="number" min={min} step={step} defaultValue={s[k]} key={`${k}-${s[k]}`} disabled={!isAdmin} className="w-24 h-9 rounded-lg border border-slate-200 px-2 text-sm text-right"
      onBlur={(e) => { const v = parseFloat(e.target.value); if (isFinite(v) && v !== s[k]) save({ [k]: v }); }} data-testid={`alert-${k}`} />
  );

  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 space-y-4" data-testid="alert-settings-section">
      <div className="flex items-start gap-3">
        <div className="cp-dark w-9 h-9 rounded-xl bg-[#171B21] text-white grid place-items-center flex-shrink-0"><Music2 className="w-4 h-4" /></div>
        <div>
          <h3 className="text-sm font-bold text-slate-900">Celebration alerts</h3>
          <p className="text-xs text-slate-500 mt-0.5">Full-screen alert with confetti and your track for every lodgement, approval and settlement. The track is stored in the app, so every screen and office TV plays it automatically. Only play a track you&apos;re licensed to use in the office.</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="alert-track-input" />
        <button type="button" disabled={!isAdmin || busy} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-[#171B21] text-white text-sm font-semibold disabled:opacity-50" data-testid="alert-track-choose">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {s.track_name ? 'Replace track' : 'Choose track'}
        </button>
        <span className="text-sm text-slate-600 truncate max-w-[260px]" data-testid="alert-track-name">{s.track_name ? `${s.track_name} · ${(s.track_size / 1048576).toFixed(1)} MB` : 'No track loaded'}</span>
        {s.track_name && isAdmin && <button type="button" onClick={remove} disabled={busy} className="inline-flex items-center gap-1 text-xs text-rose-600 font-semibold" data-testid="alert-track-remove"><Trash2 className="w-3.5 h-3.5" /> Remove</button>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <label className="grid gap-1"><span className="text-xs text-slate-500">Start at (seconds)</span>{num('start', 0)}</label>
        <label className="grid gap-1"><span className="text-xs text-slate-500">Stop at (seconds)</span>{num('end', 1)}</label>
        <label className="grid gap-1"><span className="text-xs text-slate-500">Volume (0 to 1)</span>{num('vol', 0, 0.05)}</label>
        <label className="grid gap-1"><span className="text-xs text-slate-500">Play sound</span>
          <input type="checkbox" checked={!!s.on} disabled={!isAdmin} onChange={(e) => save({ on: e.target.checked })} className="mt-2" data-testid="alert-sound-on" /></label>
      </div>

      <div className="flex flex-wrap gap-4 text-sm">
        {[['lodged', 'Alert on lodgements'], ['approved', 'Alert on approvals'], ['settled', 'Alert on settlements']].map(([k, l]) => (
          <label key={k} className="inline-flex items-center gap-2 text-slate-700"><input type="checkbox" checked={s.types?.[k] !== false} disabled={!isAdmin} onChange={(e) => save({ types: { [k]: e.target.checked } })} data-testid={`alert-type-${k}`} /> {l}</label>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {[['lodged', 'Test lodgement'], ['approved', 'Test approval'], ['settled', 'Test settlement']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => cel.test(k)} className="h-9 px-4 rounded-full border border-slate-300 bg-white text-sm font-semibold text-slate-800" data-testid={`alert-test-${k}`}>{l}</button>
        ))}
      </div>
      <p className="text-xs text-slate-500">Tests show the alert with a $350,000 example. Figures don&apos;t change. {cel.sound.localMuted ? 'Sound is muted on this screen (speaker button in the tracker header).' : ''}</p>
    </section>
  );
}
